import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, MapPin, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    project_type: '',
    urgency: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const projectTypes = t('contact.projectTypes');
  const urgencyOptions = [
    { value: 'urgent', label: { es: 'Urgente (menos de 1 mes)', en: 'Urgent (less than 1 month)' } },
    { value: '1-3months', label: { es: '1 a 3 meses', en: '1 to 3 months' } },
    { value: '3-6months', label: { es: '3 a 6 meses', en: '3 to 6 months' } },
    { value: 'flexible', label: { es: 'Flexible / Sin prisa', en: 'Flexible / No rush' } },
  ];
  const offices = [
    { city: t('contact.offices.0.city'), country: t('contact.offices.0.country'), flag: '🇺🇸' },
    { city: t('contact.offices.1.city'), country: t('contact.offices.1.country'), flag: '🇪🇨' },
    { city: t('contact.offices.2.city'), country: t('contact.offices.2.country'), flag: '🇬🇧' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      
      // Track Google Ads conversion
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
      
      toast.success(t('contact.successTitle'), {
        description: t('contact.successDesc'),
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        project_type: '',
        urgency: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.errorTitle'), {
        description: t('contact.errorDesc'),
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setIsSubmitted(false), 3000);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0A0A0B] relative" data-testid="contact-section">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FF4D2D] rounded-full blur-[250px] opacity-[0.05]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.span 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="label-tag text-[#FF4D2D] mb-4 block"
          >
            {t('contact.label')}
          </motion.span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-[#FAFAFA]">
            {t('contact.title1')}{' '}
            <span className="text-[#FF4D2D]">{t('contact.title2')}</span>
          </h2>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-[#A1A1AA] text-lg mt-4 max-w-2xl mx-auto"
          >
            {t('contact.subtitle')}
          </motion.p>
        </motion.div>

        {/* Offices - Global Presence */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-center label-tag text-[#A1A1AA] mb-8">
            {t('contact.globalPresence')}
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {offices.map((office, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-[#18181B] border border-[#27272A] rounded-2xl p-6 text-center group hover:border-[#FF4D2D]/30 transition-all duration-300"
                data-testid={`office-${index}`}
              >
                <motion.span 
                  className="text-4xl mb-3 block"
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {office.flag}
                </motion.span>
                <h3 className="text-xl font-semibold text-[#FAFAFA] mb-1 tracking-tight">
                  {office.city}
                </h3>
                <p className="text-[#A1A1AA] text-sm">{office.country}</p>
                <div className="w-0 h-0.5 bg-[#FF4D2D] mx-auto mt-4 group-hover:w-12 transition-all duration-500" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-3"
          >
            <div className="bg-[#0A0A0B] rounded-2xl p-8 md:p-10">
              {/* Terminal Header */}
              <div className="flex items-center gap-2 mb-8 pb-4 border-b border-[#27272A]">
                <div className="w-3 h-3 rounded-full bg-[#FF5F56]" />
                <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                <div className="w-3 h-3 rounded-full bg-[#27C93F]" />
                <span className="ml-3 text-xs text-[#A1A1AA] font-mono">contact@aj-code-labs ~ </span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-mono text-[#A1A1AA] mb-2">
                      <span className="text-[#FF4D2D]">$</span> {t('contact.form.name')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#18181B] border border-[#27272A] rounded-xl px-4 py-3 text-[#FAFAFA] placeholder:text-[#52525B] focus:outline-none focus:border-[#FF4D2D] transition-colors"
                      placeholder={t('contact.form.namePlaceholder')}
                      data-testid="contact-name-input"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-mono text-[#A1A1AA] mb-2">
                      <span className="text-[#FF4D2D]">$</span> {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#18181B] border border-[#27272A] rounded-xl px-4 py-3 text-[#FAFAFA] placeholder:text-[#52525B] focus:outline-none focus:border-[#FF4D2D] transition-colors"
                      placeholder={t('contact.form.emailPlaceholder')}
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Phone/WhatsApp */}
                  <div>
                    <label className="block text-sm font-mono text-[#A1A1AA] mb-2">
                      <span className="text-[#FF4D2D]">$</span> {t('contact.form.phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#18181B] border border-[#27272A] rounded-xl px-4 py-3 text-[#FAFAFA] placeholder:text-[#52525B] focus:outline-none focus:border-[#FF4D2D] transition-colors"
                      placeholder={t('contact.form.phonePlaceholder')}
                      data-testid="contact-phone-input"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-mono text-[#A1A1AA] mb-2">
                      <span className="text-[#FF4D2D]">$</span> {t('contact.form.company')} <span className="text-[#52525B]">{t('contact.form.companyOptional')}</span>
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full bg-[#18181B] border border-[#27272A] rounded-xl px-4 py-3 text-[#FAFAFA] placeholder:text-[#52525B] focus:outline-none focus:border-[#FF4D2D] transition-colors"
                      placeholder={t('contact.form.companyPlaceholder')}
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Project Type */}
                  <div>
                    <label className="block text-sm font-mono text-[#A1A1AA] mb-2">
                      <span className="text-[#FF4D2D]">$</span> {t('contact.form.projectType')}
                    </label>
                    <select
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#18181B] border border-[#27272A] rounded-xl px-4 py-3 text-[#FAFAFA] focus:outline-none focus:border-[#FF4D2D] transition-colors cursor-pointer"
                      data-testid="contact-project-type-select"
                    >
                      <option value="" disabled className="bg-[#18181B]">
                        {t('contact.form.projectPlaceholder')}
                      </option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type} className="bg-[#18181B]">
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Urgency */}
                  <div>
                    <label className="block text-sm font-mono text-[#A1A1AA] mb-2">
                      <span className="text-[#FF4D2D]">$</span> {t('contact.form.urgency')}
                    </label>
                    <select
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#18181B] border border-[#27272A] rounded-xl px-4 py-3 text-[#FAFAFA] focus:outline-none focus:border-[#FF4D2D] transition-colors cursor-pointer"
                      data-testid="contact-urgency-select"
                    >
                      <option value="" disabled className="bg-[#18181B]">
                        {t('contact.form.urgencyPlaceholder')}
                      </option>
                      {urgencyOptions.map((option) => (
                        <option key={option.value} value={option.value} className="bg-[#18181B]">
                          {option.label[language]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-mono text-[#A1A1AA] mb-2">
                    <span className="text-[#FF4D2D]">$</span> {t('contact.form.message')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full bg-[#18181B] border border-[#27272A] rounded-xl px-4 py-3 text-[#FAFAFA] placeholder:text-[#52525B] focus:outline-none focus:border-[#FF4D2D] transition-colors resize-none"
                    placeholder={t('contact.form.messagePlaceholder')}
                    data-testid="contact-message-textarea"
                  />
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FAFAFA] text-[#0A0A0B] py-4 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('contact.form.sending')}
                    </>
                  ) : isSubmitted ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      {t('contact.form.sent')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {t('contact.form.submit')}
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Email Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="bg-[#0A0A0B] rounded-2xl p-6 group transition-all duration-300"
              data-testid="contact-info-email"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FF4D2D]/20 flex items-center justify-center shrink-0 group-hover:bg-[#FF4D2D]/30 transition-colors duration-300">
                  <Mail className="w-5 h-5 text-[#FF4D2D]" />
                </div>
                <div>
                  <p className="text-sm font-mono text-[#A1A1AA] mb-1">{t('contact.emailLabel')}</p>
                  <a
                    href="mailto:contacto@ajcodelabs.ai"
                    className="text-[#FAFAFA] hover:text-[#FF4D2D] transition-colors duration-300"
                  >
                    contacto@ajcodelabs.ai
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Headquarters Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="bg-[#0A0A0B] rounded-2xl p-6 group transition-all duration-300"
              data-testid="contact-info-hq"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FF4D2D]/20 flex items-center justify-center shrink-0 group-hover:bg-[#FF4D2D]/30 transition-colors duration-300">
                  <MapPin className="w-5 h-5 text-[#FF4D2D]" />
                </div>
                <div>
                  <p className="text-sm font-mono text-[#A1A1AA] mb-1">{t('contact.hqLabel')}</p>
                  <p className="text-[#FAFAFA]">Miami, Florida</p>
                  <p className="text-[#A1A1AA] text-sm">{t('contact.hqExtra')}</p>
                </div>
              </div>
            </motion.div>

            {/* Availability Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ scale: 1.02 }}
              className="bg-[#0A0A0B] rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <motion.div 
                  className="w-3 h-3 rounded-full bg-[#27C93F]"
                  animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span className="text-[#FAFAFA] font-medium">{t('contact.available')}</span>
              </div>
              <p className="text-sm text-[#A1A1AA]">
                {t('contact.availableDesc')}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
